import { useEffect, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { Broom, PushPin, PushPinSlash } from "@phosphor-icons/react";

function expiresLabel(iso) {
  if (!iso) return "no expiry";
  const diff = new Date(iso).getTime() - Date.now();
  const hrs = Math.round(Math.abs(diff) / 3600000);
  if (diff < 0) return hrs < 48 ? `expired ${hrs}h ago` : `expired ${Math.round(hrs / 24)}d ago`;
  return hrs < 1 ? "expires in <1h" : `expires in ${hrs}h`;
}

function SandboxRow({ t, muted, onPin }) {
  return (
    <div className={`flex items-center justify-between gap-2 py-1.5 text-xs ${muted ? "text-muted-foreground" : ""}`} data-testid={`sandbox-row-${t.slug}`}>
      <div className="min-w-0">
        <span className="font-medium truncate">{t.name}</span>
        <span className="ml-2 font-mono text-[10px] text-muted-foreground">{t.slug}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[10px] text-muted-foreground">{expiresLabel(t.sandbox_expires_at)}</span>
        <button type="button" onClick={() => onPin(t, !t.sandbox_pinned)} className="text-muted-foreground hover:text-foreground" title={t.sandbox_pinned ? "Unpin — allow purge" : "Pin — never purge"} data-testid={`sandbox-pin-${t.slug}`}>
          {t.sandbox_pinned ? <PushPinSlash size={13} /> : <PushPin size={13} />}
        </button>
      </div>
    </div>
  );
}

export function PurgeSandboxesDialog({ open, onClose, onPurged }) {
  const [data, setData] = useState(null);
  const [includeActive, setIncludeActive] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/platform/sandboxes").then(({ data }) => setData(data)).catch(() => toast.error("Could not load sandboxes"));

  useEffect(() => {
    if (!open) return;
    setData(null);
    setIncludeActive(false);
    load();
  }, [open]);

  const pin = async (t, pinned) => {
    try {
      await API.put(`/tenants/${t.id}/sandbox-pin`, { pinned });
      toast.success(pinned ? `${t.name} pinned — it will never be purged` : `${t.name} unpinned`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update pin"); }
  };

  const purge = async () => {
    setBusy(true);
    try {
      const { data: res } = await API.post("/platform/sandboxes/purge-expired", { include_active: includeActive }, { timeout: 300000 });
      toast.success(`Purged ${res.purged_count} sandbox${res.purged_count === 1 ? "" : "es"} (${res.total_docs} records)`);
      onPurged(res);
    } catch (e) { toast.error(e.response?.data?.detail || "Purge failed"); }
    finally { setBusy(false); }
  };

  const total = data ? data.counts.expired + (includeActive ? data.counts.active : 0) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="purge-sandboxes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Broom size={20} /> Purge sandbox tenants</DialogTitle>
          <DialogDescription>Demo sandboxes expire 24h after launch and are removed automatically every hour. Purge them now, or pin any you want to keep.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Loading sandboxes…</div>
        ) : (
          <div className="space-y-4">
            <section>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-red-600 mb-1" data-testid="purge-expired-count">Expired · {data.counts.expired}</p>
              <div className="max-h-40 overflow-y-auto divide-y divide-border rounded-md border border-border px-3">
                {data.expired.length === 0 && <p className="py-3 text-xs text-muted-foreground">Nothing expired right now.</p>}
                {data.expired.map((t) => <SandboxRow key={t.id} t={t} onPin={pin} />)}
              </div>
            </section>
            {data.counts.active > 0 && (
              <section>
                <label className="flex items-center gap-2 text-xs cursor-pointer mb-1" data-testid="purge-include-active">
                  <Checkbox checked={includeActive} onCheckedChange={(v) => setIncludeActive(!!v)} data-testid="purge-include-active-checkbox" />
                  Also remove {data.counts.active} sandbox{data.counts.active === 1 ? "" : "es"} still inside the 24h window
                </label>
                <div className="max-h-32 overflow-y-auto divide-y divide-border rounded-md border border-border px-3">
                  {data.active.map((t) => <SandboxRow key={t.id} t={t} muted={!includeActive} onPin={pin} />)}
                </div>
              </section>
            )}
            {data.counts.pinned > 0 && (
              <section>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Pinned · kept forever · {data.counts.pinned}</p>
                <div className="divide-y divide-border rounded-md border border-border px-3">
                  {data.pinned.map((t) => <SandboxRow key={t.id} t={t} muted onPin={pin} />)}
                </div>
              </section>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy} data-testid="purge-cancel-btn">Close</Button>
              <Button variant="destructive" className="flex-1" onClick={purge} disabled={busy || total === 0} data-testid="purge-confirm-btn">
                <Broom size={14} className="mr-1" /> {busy ? "Purging…" : `Purge ${total} sandbox${total === 1 ? "" : "es"}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
